import { Form } from 'antd';
import { useCallback, useMemo, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';

import {
  useCreateWarehouseInventoryMutation,
  useDeleteWarehouseInventoryMutation,
  useEditWarehouseInventoryMutation,
  useGetWarehouseInventoryByIdQuery,
  type ModelCreateWarehouseInventory,
} from '@app/api/WarehouseInventories';
import { INVENTORY_PATH } from '@app/constants';
import { useNewUserAccessRights, useUserPrint } from '@app/hooks';
import type { AddEditInventoryProps } from '@app/pages/Warehouses/Inventory/components/AddEditInventory/types';
import { useNotification } from '@app/providers/NotificationProvider/NotificationContext';
import { useGetWarehousesQuery } from '@app/services/rtkQuery/Warehouses/api';
import { useErrorHandler } from '@app/shared/hooks';
import type { Options } from '@app/types';

const { useForm } = Form;

export const useAddEditInventory = ({ mode }: AddEditInventoryProps) => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { activeBranchId: ActiveBranchId } = useUserPrint();
  const { showNotification } = useNotification();
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const {
    userAccessRights: {
      Warehouse: { IsInventoryCreate, IsInventoryEdit, IsInventoryDelete },
    },
  } = useNewUserAccessRights();
  const [form] = useForm<ModelCreateWarehouseInventory>();
  const { resetFields, setFieldsValue } = form;

  const inventoryId = useMemo(() => {
    if (mode !== 'edit') {
      return null;
    }

    const id = searchParams.get('id');

    if (id) {
      const parsedId = parseInt(id, 10);
      if (isNaN(parsedId)) {
        showNotification(
          'Некорректный идентификатор бренда. Пожалуйста, проверьте URL.',
          'frontWarning'
        );
        return null;
      }
      return parsedId;
    }
    return null;
  }, [searchParams, mode, showNotification]);

  const [
    createWarehouseInventory,
    {
      isLoading: isCreateInventoryLoading,
      isError: isCreateInventoryError,
      error: createInventoryError,
    },
  ] = useCreateWarehouseInventoryMutation();

  const [
    deleteWarehouseInventory,
    {
      isLoading: isDeleteInventoryLoading,
      isError: isDeleteInventoryError,
      error: deleteInventoryError,
    },
  ] = useDeleteWarehouseInventoryMutation();

  const [
    editWarehouseInventory,
    {
      isLoading: isEditInventoryLoading,
      isError: isEditInventoryError,
      error: editInventoryError,
    },
  ] = useEditWarehouseInventoryMutation();

  const {
    data: warehouses,
    isLoading: isLoadingWarehouses,
    isUninitialized: isUninitializedWarehouses,
    isFetching: isFetchingWarehouses,
    isError: isErrorWarehouses,
    error: errorWarehouses,
  } = useGetWarehousesQuery({ ActiveBranchId }, { skip: !ActiveBranchId });

  const {
    data: inventoryData,
    isLoading: isLoadingInventory,
    isFetching: isFetchingInventory,
    isError: isErrorInventory,
    error: errorInventory,
  } = useGetWarehouseInventoryByIdQuery(
    {
      inventoryId: inventoryId ?? 0,
      ActiveBranchId,
    },
    {
      skip: !inventoryId || !ActiveBranchId || mode !== 'edit',
    }
  );

  useErrorHandler([
    { flag: isCreateInventoryError, error: createInventoryError },
    { flag: isDeleteInventoryError, error: deleteInventoryError },
    { flag: isEditInventoryError, error: editInventoryError },
    { flag: isErrorWarehouses, error: errorWarehouses },
    { flag: isErrorInventory, error: errorInventory },
  ]);

  const disabledForm = useMemo(() => {
    switch (mode) {
      case 'add':
        return (
          isFetchingWarehouses ||
          isLoadingWarehouses ||
          isUninitializedWarehouses ||
          isCreateInventoryLoading
        );
      case 'edit':
        return (
          isFetchingWarehouses ||
          isLoadingWarehouses ||
          isUninitializedWarehouses ||
          isEditInventoryLoading ||
          isDeleteInventoryLoading ||
          isCreateInventoryLoading ||
          isLoadingInventory ||
          isFetchingInventory
        );
      default:
        return false;
    }
  }, [
    isLoadingWarehouses,
    isFetchingWarehouses,
    isUninitializedWarehouses,
    isCreateInventoryLoading,
    isEditInventoryLoading,
    isDeleteInventoryLoading,
    mode,
    isLoadingInventory,
    isFetchingInventory,
  ]);

  const warehousesOptions: Options = useMemo(
    () =>
      warehouses?.map(({ Name, Id }) => ({
        label: Name,
        value: Id,
      })) || [],
    [warehouses]
  );

  const goToInventory = useCallback(() => {
    navigate(INVENTORY_PATH);
  }, [navigate]);

  const onCancel = useCallback(() => {
    goToInventory();
    resetFields();
    setIsDeleteOpen(false);
  }, [goToInventory, resetFields]);

  const onDelete = useCallback(async () => {
    if (!inventoryId) {
      return showNotification(
        'Идентификатор инвентаризации не указан.',
        'frontWarning'
      );
    }

    try {
      await deleteWarehouseInventory({
        inventoryId,
        ActiveBranchId,
      }).unwrap();

      showNotification('Инвентаризация успешно удалена.', 'success');

      onCancel();
    } catch (error) {
      showNotification(error);
    }
  }, [
    inventoryId,
    deleteWarehouseInventory,
    showNotification,
    onCancel,
    ActiveBranchId,
  ]);

  const inventoryDataHandler = useCallback(() => {
    if (inventoryData) {
      const { WarehouseId, Comment } = inventoryData;
      setFieldsValue({
        Comment,
        WarehouseId,
      });
    } else {
      resetFields();
    }
  }, [inventoryData, resetFields, setFieldsValue]);

  const editWarehouseInventoryHandler = useCallback(
    async (values: ModelCreateWarehouseInventory) => {
      if (!inventoryId) {
        return showNotification(
          'Идентификатор инвентаризации не указан.',
          'frontWarning'
        );
      }
      try {
        await editWarehouseInventory({
          ActiveBranchId,
          data: {
            Id: inventoryId,
            ...values,
          },
        }).unwrap();

        showNotification('Инвентаризация успешно обновлена.', 'success');

        onCancel();
      } catch (error) {
        showNotification('Ошибка при обновлении инвентаризации.', 'warning');
      }
    },
    [
      inventoryId,
      editWarehouseInventory,
      showNotification,
      onCancel,
      ActiveBranchId,
    ]
  );

  const createWarehouseInventoryHandler = useCallback(
    async (data: ModelCreateWarehouseInventory) => {
      try {
        await createWarehouseInventory({
          ActiveBranchId,
          data,
        }).unwrap();

        showNotification('Инвентаризация успешно создана.', 'success');

        onCancel();
      } catch (error) {
        showNotification('Ошибка при создании инвентаризации.', 'warning');
      }
    },
    [createWarehouseInventory, showNotification, onCancel, ActiveBranchId]
  );

  const onFinish = useCallback(
    async (values: ModelCreateWarehouseInventory) => {
      if (mode === 'add') {
        return createWarehouseInventoryHandler(values);
      }

      if (mode === 'edit') {
        return editWarehouseInventoryHandler(values);
      }

      return Promise.reject(new Error('Неверный режим работы'));
    },
    [mode, createWarehouseInventoryHandler, editWarehouseInventoryHandler]
  );
  const openDeleteModal = useCallback(() => {
    setIsDeleteOpen(true);
  }, []);

  const closeDeleteModal = useCallback(() => {
    setIsDeleteOpen(false);
  }, []);

  return {
    form,
    disabledForm,
    onCancel,
    openDeleteModal,
    closeDeleteModal,
    warehousesOptions,
    isDeleteOpen,
    onDelete,
    inventoryDataHandler,
    onFinish,
    IsInventoryCreate,
    IsInventoryEdit,
    IsInventoryDelete,
  };
};
